import { useEffect, useRef, useState, useCallback } from 'react'
import maplibregl from 'maplibre-gl'
import {
  Layers,
  ZoomIn,
  ZoomOut,
  Home,
  MousePointer,
  MousePointer2,
  Square,
  X,
} from 'lucide-react'
import { useUI } from '@/lib/store'
import { MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM, MAP_STYLE_FREE, getProgressColor, ZONE_COLORS } from '@/lib/constants'
import ElementPanel from './ElementPanel'
import type { MapMode } from '@/types'

// =============================================================================
// Layer toggle state
// =============================================================================

interface LayerState {
  trackers: boolean
  piles:    boolean
  parcels:  boolean
  labels:   boolean
}

// =============================================================================
// MapView Component
// =============================================================================

export default function MapView() {
  const mapContainerRef               = useRef<HTMLDivElement>(null)
  const mapRef                        = useRef<maplibregl.Map | null>(null)
  const { mapMode, setMapMode, setSelectedElement, selectedElement } = useUI()

  const [layers, setLayers]           = useState<LayerState>({
    trackers: true,
    piles:    true,
    parcels:  true,
    labels:   true,
  })
  const [mapLoaded, setMapLoaded]     = useState(false)
  const [layerPanelOpen, setLayerPanelOpen] = useState(false)
  const [zoom, setZoom]               = useState(MAP_DEFAULT_ZOOM)
  const [panelOpen, setPanelOpen]     = useState(false)

  // ---------------------------------------------------------------------------
  // Initialize map
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const map = new maplibregl.Map({
      container:  mapContainerRef.current,
      style:      MAP_STYLE_FREE,
      center:     MAP_DEFAULT_CENTER,
      zoom:       MAP_DEFAULT_ZOOM,
      attributionControl: false,
    })

    // Controls
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left')

    map.on('load', () => {
      setMapLoaded(true)

      // ---------------------------------------------------------------------------
      // Add placeholder parcel boundaries layer (real data from spatial query)
      // ---------------------------------------------------------------------------
      map.addSource('parcels', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id:     'parcels-fill',
        type:   'fill',
        source: 'parcels',
        paint:  {
          'fill-color':   ['coalesce', ['get', 'fill_color'], '#E5E7EB'],
          'fill-opacity': 0.2,
        },
      })
      map.addLayer({
        id:     'parcels-outline',
        type:   'line',
        source: 'parcels',
        paint:  {
          'line-color': '#6B7280',
          'line-width': 1.5,
        },
      })

      // ---------------------------------------------------------------------------
      // Tracker polygons layer
      // ---------------------------------------------------------------------------
      map.addSource('trackers', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id:     'trackers-fill',
        type:   'fill',
        source: 'trackers',
        paint:  {
          'fill-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false], '#3B82F6',
            ['coalesce', ['get', 'fill_color'], '#9CA3AF'],
          ],
          'fill-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false], 0.8,
            0.5,
          ],
        },
      })
      map.addLayer({
        id:     'trackers-outline',
        type:   'line',
        source: 'trackers',
        paint:  {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false], '#1D4ED8',
            '#94A3B8',
          ],
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'selected'], false], 2.5,
            0.8,
          ],
        },
      })

      // ---------------------------------------------------------------------------
      // Tracker labels
      // ---------------------------------------------------------------------------
      map.addLayer({
        id:          'tracker-labels',
        type:        'symbol',
        source:      'trackers',
        minzoom:     16,
        layout: {
          'text-field':     ['get', 'tracker_code'],
          'text-size':      10,
          'text-font':      ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor':    'center',
          'symbol-placement': 'point',
        },
        paint: {
          'text-color': '#1E293B',
          'text-halo-color': '#FFFFFF',
          'text-halo-width': 1,
        },
      })

      // ---------------------------------------------------------------------------
      // Pile points
      // ---------------------------------------------------------------------------
      map.addSource('piles', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id:      'piles-circle',
        type:    'circle',
        source:  'piles',
        minzoom: 15,
        paint:   {
          'circle-radius':       5,
          'circle-color':        ['coalesce', ['get', 'color'], '#2563EB'],
          'circle-stroke-width': 1,
          'circle-stroke-color': '#FFFFFF',
          'circle-opacity':      0.85,
        },
      })
    })

    // Zoom listener
    map.on('zoom', () => setZoom(Math.round(map.getZoom() * 10) / 10))

    // ---------------------------------------------------------------------------
    // Click handlers
    // ---------------------------------------------------------------------------
    let hoveredId: string | number | null = null

    map.on('mousemove', 'trackers-fill', (e) => {
      map.getCanvas().style.cursor = 'pointer'
      if (e.features?.length) {
        if (hoveredId !== null) {
          map.setFeatureState({ source: 'trackers', id: hoveredId }, { hover: false })
        }
        hoveredId = e.features[0].id ?? null
        if (hoveredId !== null) {
          map.setFeatureState({ source: 'trackers', id: hoveredId }, { hover: true })
        }
      }
    })

    map.on('mouseleave', 'trackers-fill', () => {
      map.getCanvas().style.cursor = ''
      if (hoveredId !== null) {
        map.setFeatureState({ source: 'trackers', id: hoveredId }, { hover: false })
        hoveredId = null
      }
    })

    map.on('click', 'trackers-fill', (e) => {
      const feature = e.features?.[0]
      if (!feature) return

      const trackerId   = feature.properties?.id as string
      const trackerCode = feature.properties?.tracker_code as string

      setSelectedElement({ type: 'tracker', id: trackerId, data: { ...feature.properties } })
      setPanelOpen(true)

      // Clear previous selection
      if (selectedElement?.id) {
        map.setFeatureState({ source: 'trackers', id: selectedElement.id }, { selected: false })
      }
      if (feature.id !== undefined) {
        map.setFeatureState({ source: 'trackers', id: feature.id }, { selected: true })
      }
    })

    map.on('click', (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['trackers-fill'] })
      if (features.length === 0) {
        setPanelOpen(false)
        setSelectedElement(null)
      }
    })

    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, []) // intentionally empty — map init once

  // ---------------------------------------------------------------------------
  // Layer visibility
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    const layerIds: Record<keyof LayerState, string[]> = {
      trackers: ['trackers-fill', 'trackers-outline'],
      piles:    ['piles-circle'],
      parcels:  ['parcels-fill', 'parcels-outline'],
      labels:   ['tracker-labels'],
    }

    for (const [key, ids] of Object.entries(layerIds) as [keyof LayerState, string[]][]) {
      const vis = layers[key] ? 'visible' : 'none'
      ids.forEach((id) => {
        if (map.getLayer(id)) {
          map.setLayoutProperty(id, 'visibility', vis)
        }
      })
    }
  }, [layers, mapLoaded])

  // ---------------------------------------------------------------------------
  // Map controls
  // ---------------------------------------------------------------------------
  const zoomIn  = () => mapRef.current?.zoomIn()
  const zoomOut = () => mapRef.current?.zoomOut()
  const resetView = () => {
    mapRef.current?.flyTo({ center: MAP_DEFAULT_CENTER, zoom: MAP_DEFAULT_ZOOM, duration: 1200 })
  }

  const toggleLayer = (layer: keyof LayerState) => {
    setLayers((prev) => ({ ...prev, [layer]: !prev[layer] }))
  }

  const mapModeOptions: { mode: MapMode; icon: React.ComponentType<{ className?: string }>; label: string }[] = [
    { mode: 'VIEW',          icon: MousePointer,  label: 'Vista' },
    { mode: 'SELECT_SINGLE', icon: MousePointer2, label: 'Seleccionar' },
    { mode: 'SELECT_MULTI',  icon: Square,        label: 'Multi-selección' },
    { mode: 'ASSIGN_PARCEL', icon: Layers,        label: 'Asignar parcela' },
  ]

  return (
    <div className="relative w-full h-full flex">
      {/* Map container */}
      <div ref={mapContainerRef} className="flex-1 h-full" />

      {/* ------------------------------------------------------------------ */}
      {/* Floating controls — top left */}
      {/* ------------------------------------------------------------------ */}
      <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
        {/* Layer toggles */}
        <div className="relative">
          <button
            onClick={() => setLayerPanelOpen(!layerPanelOpen)}
            className="w-9 h-9 bg-white rounded-lg shadow-card border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
            title="Capas"
          >
            <Layers className="w-4 h-4" />
          </button>

          {layerPanelOpen && (
            <div className="absolute top-0 left-11 bg-white rounded-xl shadow-panel border border-gray-200 p-3 min-w-[160px]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-700">Capas</p>
                <button onClick={() => setLayerPanelOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {(Object.keys(layers) as (keyof LayerState)[]).map((layer) => (
                <label key={layer} className="flex items-center gap-2 py-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={layers[layer]}
                    onChange={() => toggleLayer(layer)}
                    className="w-3.5 h-3.5 accent-brand-600"
                  />
                  <span className="text-xs text-gray-700 capitalize">{
                    layer === 'trackers' ? 'Trackers' :
                    layer === 'piles'    ? 'Hincas' :
                    layer === 'parcels'  ? 'Parcelas' : 'Etiquetas'
                  }</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Mode selector */}
        <div className="flex flex-col gap-1 bg-white rounded-lg shadow-card border border-gray-200 p-1">
          {mapModeOptions.map(({ mode, icon: Icon, label }) => (
            <button
              key={mode}
              onClick={() => setMapMode(mode)}
              title={label}
              className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
                mapMode === mode
                  ? 'bg-brand-600 text-white'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Zoom controls — top right */}
      {/* ------------------------------------------------------------------ */}
      <div className="absolute top-4 right-4 flex flex-col gap-1 z-10">
        <button
          onClick={zoomIn}
          className="w-9 h-9 bg-white rounded-t-lg shadow-card border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          onClick={zoomOut}
          className="w-9 h-9 bg-white border-x border-b border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <button
          onClick={resetView}
          className="w-9 h-9 bg-white rounded-b-lg border-x border-b border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
          title="Vista inicial"
        >
          <Home className="w-4 h-4" />
        </button>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Zoom level indicator */}
      {/* ------------------------------------------------------------------ */}
      <div className="absolute bottom-8 right-4 z-10">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 text-xs text-gray-500 border border-gray-200 shadow-card">
          Zoom: {zoom.toFixed(1)}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Legend */}
      {/* ------------------------------------------------------------------ */}
      <div className="absolute bottom-8 left-4 z-10">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2.5 border border-gray-200 shadow-card">
          <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Avance</p>
          <div className="space-y-1">
            {[
              { color: '#9CA3AF', label: '0% — No iniciado' },
              { color: '#FACC15', label: '1-24%' },
              { color: '#FB923C', label: '25-49%' },
              { color: '#4ADE80', label: '50-74%' },
              { color: '#16A34A', label: '75-99%' },
              { color: '#2563EB', label: '100% — Completo' },
            ].map(({ color, label }) => (
              <div key={color} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                <span className="text-[10px] text-gray-600">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Element detail panel */}
      {/* ------------------------------------------------------------------ */}
      {panelOpen && selectedElement && (
        <ElementPanel
          elementId={selectedElement.id}
          elementType={selectedElement.type}
          onClose={() => {
            setPanelOpen(false)
            setSelectedElement(null)
          }}
        />
      )}
    </div>
  )
}
